import { useCallback, useEffect, useMemo } from 'react';
import type { Entries } from 'type-fest';

import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import StorageIcon from '@mui/icons-material/Storage';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import StoreIcon from '@mui/icons-material/Store';
import MapIcon from '@mui/icons-material/Map';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import EuroIcon from '@mui/icons-material/Euro';
import ConstructionIcon from '@mui/icons-material/Construction';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EventIcon from '@mui/icons-material/Event';
import TableChartIcon from '@mui/icons-material/TableChart';
import HomeIcon from '@mui/icons-material/Home';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import type { Palette } from '@mui/material';

import { ColorEnum, PermissionEnum } from '@e-socna/data-layer';
import { buildPath } from '@e-socna/utils';

import { Page } from '../../../contexts/NavigationContext/enums/page.enum';
import {
  PATH_FOLDERS_DEFAULT,
  PATH_HOME,
  PATH_GIS_DEFAULT,
  PATH_INTERNAL_THIRD_PARTIES_DEFAULT,
  PATH_EXTERNAL_THIRD_PARTIES_DEFAULT,
  PATH_LOCALITY_INFOS,
  PATH_USERS_DEFAULT,
  PATH_COSTS,
  PATH_MATERIALS,
  PATH_RESOLVABLE_SERVICES,
  PATH_RESOLVABLE_MISSION_TYPES,
  PATH_DOCUMENT_AND_SECTION_TEMPLATES_DEFAULT,
  PATH_REFERENCES,
  PATH_WORKFLOWS,
  PATH_DATA,
  PATH_FOLDER_DEFAULT,
  PATH_NOT_FOUND,
  PATH_FOLDER_MISSIONS,
  PATH_FOLDER_BUSINESS_EVENTS,
  PATH_FOLDER_FILES_DEFAULT,
} from '../../../app/constants/paths.constant';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useNavigationContext } from '../../../contexts/NavigationContext/hooks/useNavigationContext.hook';
import { Avatar } from '../../../components/Avatar';
import { iconToMuiIcon } from '../../../utils/icon-to-mui-icon.util';
import { filterWorkflowsOfMissionWorkflow } from '../../../utils/workflows-of-mission-workflow.util';
import { useColorToMuiColor } from '../../../hooks/useColorToMuiColor.hook';
import { usePermissions } from '../../../hooks/usePermissions.hook';

import { Space } from '../enums/space.enum';

import { useMissionMenuSpaces } from './useMissionMenuSpaces.hook';
import type { Menu } from '../types/menu.type';

const defaultAvatarColor = (palette: Readonly<Palette>) =>
  palette.mode === 'light' ? palette.grey[300] : palette.grey[900];

export const useMenu = (currentPage: Page) => {
  const {
    navigation: { folderSpaceInfo, missionSpaceInfo },
    flushFolderSpaceInfo,
    flushMissionSpaceInfo,
  } = useNavigationContext();

  const { colorToMuiColor } = useColorToMuiColor(defaultAvatarColor);

  const folderId = folderSpaceInfo?.folderId;
  const replaceFolderSpaceLink = useCallback(
    (link: string) =>
      folderId ? buildPath(link, { folderId }) : PATH_NOT_FOUND,
    [folderId],
  );

  const { folderStatesIdMap, accountingWorkflows } = useReferencesContext();
  const { can } = usePermissions();
  const canManageUsers = can(PermissionEnum.UsersManage);
  const canWriteInternalThirdParties = can(
    PermissionEnum.InternalThirdPartiesWrite,
  );
  const canLoadDataSets = can(PermissionEnum.FixturesManage);

  // A sub-mission billed by its collaboration has no accounting: no
  // accounting workflow accepts its mission workflow.
  const hasAccounting =
    !missionSpaceInfo ||
    !!filterWorkflowsOfMissionWorkflow(
      accountingWorkflows,
      missionSpaceInfo.missionWorkflowId,
    ).length;

  const missionMenuSpaces = useMissionMenuSpaces({
    missionSpaceInfo,
    hasAccounting,
    colorToMuiColor,
  });

  const menu: Menu = useMemo(() => {
    const folderState = folderSpaceInfo?.folderStateId
      ? folderStatesIdMap[folderSpaceInfo.folderStateId]
      : undefined;

    return {
      main: {
        disabled: false,
        enabledSiblings: [Space.MasterData, Space.References, Space.Admin],
        flushFolderSpaceInfoOnEnter: true,
        flushMissionSpaceInfoOnEnter: true,
        pages: [
          {
            id: Page.Home,
            title: 'Accueil',
            Icon: HomeIcon,
            to: PATH_HOME,
          },
          {
            id: Page.Gis,
            title: 'SIG',
            Icon: MapIcon,
            to: PATH_GIS_DEFAULT,
          },
          {
            id: Page.ExternalThirdParties,
            title: 'Tiers',
            Icon: PermContactCalendarIcon,
            to: PATH_EXTERNAL_THIRD_PARTIES_DEFAULT,
          },
          {
            id: Page.Folders,
            title: 'Dossiers',
            Icon: FolderCopyIcon,
            to: PATH_FOLDERS_DEFAULT,
          },
        ],
      },
      folder: {
        disabled: !folderSpaceInfo && !missionSpaceInfo,
        enabledSiblings: [
          Space.Main,
          Space.MasterData,
          Space.References,
          Space.Admin,
        ],
        flushFolderSpaceInfoOnEnter: false,
        flushMissionSpaceInfoOnEnter: true,
        pages: [
          {
            id: Page.Folder,
            title: 'Dossier',
            Icon: FolderOpenIcon,
            to: replaceFolderSpaceLink(PATH_FOLDER_DEFAULT),
            secondaryAction: folderState ? (
              <Avatar
                bgColor={colorToMuiColor(folderState.chip.color)}
                icon={iconToMuiIcon(folderState.chip.icon)}
                label={`État : ${folderState.label}`}
              />
            ) : undefined,
          },
          {
            id: Page.FolderMissions,
            title: 'Missions',
            Icon: BusinessCenterIcon,
            to: replaceFolderSpaceLink(PATH_FOLDER_MISSIONS),
            secondaryAction: folderSpaceInfo ? (
              <Avatar
                bgColor={colorToMuiColor(
                  folderSpaceInfo.missionsCount
                    ? ColorEnum.Primary
                    : ColorEnum.Default,
                )}
                label="Nombre de Missions"
                shortName={folderSpaceInfo.missionsCount.toFixed()}
              />
            ) : undefined,
          },
          {
            id: Page.FolderBusinessEvents,
            title: 'Événements Métier',
            Icon: EventIcon,
            to: replaceFolderSpaceLink(PATH_FOLDER_BUSINESS_EVENTS),
            secondaryAction: folderSpaceInfo ? (
              <Avatar
                bgColor={colorToMuiColor(
                  folderSpaceInfo.businessEventsCount
                    ? ColorEnum.Primary
                    : ColorEnum.Default,
                )}
                label="Nombre d'Événements Métier"
                shortName={folderSpaceInfo.businessEventsCount.toFixed()}
              />
            ) : undefined,
          },
          {
            id: Page.FolderFiles,
            title: 'Fichiers',
            Icon: AttachFileIcon,
            to: replaceFolderSpaceLink(PATH_FOLDER_FILES_DEFAULT),
            secondaryAction: folderSpaceInfo ? (
              <Avatar
                bgColor={colorToMuiColor(
                  folderSpaceInfo.filesCount
                    ? ColorEnum.Primary
                    : ColorEnum.Default,
                )}
                label="Nombre de Fichiers"
                shortName={folderSpaceInfo.filesCount.toFixed()}
              />
            ) : undefined,
          },
        ],
      },
      mission: missionMenuSpaces.mission,
      subMission: missionMenuSpaces.subMission,
      masterData: {
        disabled: false,
        enabledSiblings: [
          Space.Main,
          Space.MasterData,
          Space.References,
          Space.Admin,
        ],
        flushFolderSpaceInfoOnEnter: true,
        flushMissionSpaceInfoOnEnter: true,
        pages: [
          {
            id: Page.Costs,
            title: 'Coûts',
            Icon: EuroIcon,
            to: PATH_COSTS,
          },
          {
            id: Page.Materials,
            title: 'Matériels',
            Icon: ConstructionIcon,
            to: PATH_MATERIALS,
          },
          {
            id: Page.ResolvableServices,
            title: 'Services',
            Icon: RoomServiceIcon,
            to: PATH_RESOLVABLE_SERVICES,
          },
          {
            id: Page.DocumentAndSectionTemplates,
            title: 'Modèles',
            Icon: TextSnippetIcon,
            to: PATH_DOCUMENT_AND_SECTION_TEMPLATES_DEFAULT,
          },
          {
            id: Page.ResolvableMissionTypes,
            title: 'Types de Missions',
            Icon: BusinessCenterIcon,
            to: PATH_RESOLVABLE_MISSION_TYPES,
          },
        ],
      },
      references: {
        disabled: false,
        enabledSiblings: [
          Space.Main,
          Space.MasterData,
          Space.References,
          Space.Admin,
        ],
        flushFolderSpaceInfoOnEnter: true,
        flushMissionSpaceInfoOnEnter: true,
        pages: [
          {
            id: Page.References,
            title: 'Référentiel',
            Icon: StorageIcon,
            to: PATH_REFERENCES,
          },
          {
            id: Page.Workflows,
            title: 'Workflows',
            Icon: AccountTreeIcon,
            to: PATH_WORKFLOWS,
          },
          {
            id: Page.LocalityInfos,
            title: 'Infos Communes',
            Icon: MapIcon,
            to: PATH_LOCALITY_INFOS,
          },
        ],
      },
      admin: {
        // Each page greyed without its permission.
        disabled:
          !canManageUsers && !canWriteInternalThirdParties && !canLoadDataSets,
        enabledSiblings: [
          Space.Main,
          Space.MasterData,
          Space.References,
          Space.Admin,
        ],
        flushFolderSpaceInfoOnEnter: true,
        flushMissionSpaceInfoOnEnter: true,
        pages: [
          {
            id: Page.Users,
            title: 'Utilisateurs',
            Icon: SupervisedUserCircleIcon,
            to: PATH_USERS_DEFAULT,
            disabled: !canManageUsers,
          },
          {
            id: Page.InternalThirdParties,
            title: 'Organisation SOCNA',
            Icon: StoreIcon,
            to: PATH_INTERNAL_THIRD_PARTIES_DEFAULT,
            disabled: !canWriteInternalThirdParties,
          },
          {
            id: Page.Data,
            title: 'Données',
            Icon: TableChartIcon,
            to: PATH_DATA,
            disabled: !canLoadDataSets,
          },
        ],
      },
    } as const;
  }, [
    colorToMuiColor,
    folderSpaceInfo,
    folderStatesIdMap,
    canLoadDataSets,
    canManageUsers,
    canWriteInternalThirdParties,
    missionMenuSpaces,
    missionSpaceInfo,
    replaceFolderSpaceLink,
  ]);

  const [currentSpace, currentMenuSpaceInfo] = useMemo(() => {
    const entry = (Object.entries(menu) as Entries<Menu>).find(
      ([_, menuSpaceInfo]) =>
        menuSpaceInfo.pages.some((spacePage) => spacePage.id === currentPage),
    );

    if (!entry) {
      return [undefined, undefined];
    }

    return entry;
  }, [menu, currentPage]);

  useEffect(() => {
    if (
      currentMenuSpaceInfo &&
      !currentMenuSpaceInfo.flushFolderSpaceInfoOnEnter
    ) {
      return;
    }

    flushFolderSpaceInfo();
  }, [currentMenuSpaceInfo, flushFolderSpaceInfo]);

  useEffect(() => {
    if (
      currentMenuSpaceInfo &&
      !currentMenuSpaceInfo.flushMissionSpaceInfoOnEnter
    ) {
      return;
    }

    flushMissionSpaceInfo();
  }, [currentMenuSpaceInfo, flushMissionSpaceInfo]);

  return { menu, currentSpace, currentMenuSpaceInfo };
};
